import React from "react";
import cx from "classnames";

import styles from "./button.module.scss";
import IconContainer from "../IconContainer/IconContainer";
import Shimmer from "../Shimmer/Shimmer";
import { polymorphic, classMapper } from "../utils";

const classMap = {
  theme: {
    primary: styles.buttonPrimary,
    secondary: styles.buttonSecondary,
    tertiary: styles.buttonTertiary,
    "tertiary-on-gray": styles.buttonTertiaryOnGray,
    danger: styles.buttonDanger,
    "primary-transparent": styles.buttonPrimaryTransparent,
    "secondary-transparent": styles.buttonSecondaryTransparent,
    "tertiary-transparent": styles.buttonTertiaryTransparent,
    "danger-transparent": styles.buttonDangerTransparent
  },
  size: {
    small: styles.buttonSmall,
    medium: styles.buttonMedium,
    large: styles.buttonLarge
  },
  padding: {
    none: styles.buttonNonePadding,
    condensed: styles.buttonCondensedPadding,
    standard: styles.buttonStandardPadding
  },
  contentAlignment: {
    center: styles.buttonContentCenter,
    "space-between": styles.buttonContentSpaceBetween
  },
  iconAffix: {
    prefix: styles.buttonIconPrefix,
    suffix: styles.buttonIconSuffix
  }
};

export namespace Button {
  export type Props = {
    block?: boolean;
    circle?: boolean;
    className?: string;
    contentAlignment?: keyof typeof classMap.contentAlignment;
    disabled?: boolean;
    icon?: React.ElementType;
    iconAffix?: keyof typeof classMap.iconAffix;
    iconStrokeWidth?: "thin" | "thick";
    onClick?: (event: React.MouseEvent) => void;
    padding?: keyof typeof classMap.padding;
    shimmer?: boolean;
    size?: keyof typeof classMap.size;
    theme?: keyof typeof classMap.theme;
    type?: "button" | "submit" | "reset";
  };
}

export const Button = polymorphic("button")<Button.Props>(
  ({
    as: Component,
    block = false,
    children,
    className,
    disabled = false,
    circle = false,
    contentAlignment = "center",
    icon,
    iconAffix = "prefix",
    iconStrokeWidth = "thick",
    padding = "standard",
    shimmer = false,
    size = "medium",
    theme = "primary",
    type = "button",
    ...props
  }) => {
    const classList = cx(
      className,
      styles.button,
      block && styles.buttonBlock,
      ...classMapper(classMap, {
        contentAlignment,
        iconAffix: icon && iconAffix,
        padding: padding && padding,
        size,
        theme
      }),
      circle && styles.buttonCircle,
      icon && children && styles.buttonHasIconAffix,
      disabled && styles.buttonDisabled
    );

    return (
      <Component
        {...props}
        className={classList}
        disabled={disabled}
        type={Component === "button" ? type : undefined}
      >
        <span className={styles.buttonInner}>
          {shimmer && <Shimmer />}
          {icon && (
            <IconContainer
              icon={icon}
              className={styles.buttonIconContainer}
              positioning={false}
              strokeWidth={iconStrokeWidth}
            />
          )}
          {children}
        </span>
      </Component>
    );
  }
);
